/**
 * useDemoMode — manages the vulnerable/CSK toggle state.
 *
 * Single responsibility: track which mode the demo is in and
 * provide a derived `isCSK` boolean for conditional rendering.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import { useState, useCallback } from "react";
import type { DemoMode } from "../types.js";

export interface DemoModeState {
  readonly mode: DemoMode;
  readonly isCSK: boolean;
  readonly setMode: (mode: DemoMode) => void;
  readonly toggle: () => void;
}

/**
 * Manages the vulnerable / CSK-protected mode toggle.
 *
 * @param initial - Starting mode; defaults to "vulnerable" so demos
 *   open showing the attack succeeding before the user switches.
 */
export function useDemoMode(initial: DemoMode = "vulnerable"): DemoModeState {
  const [mode, setMode] = useState<DemoMode>(initial);

  return {
    mode,
    isCSK: mode === "csk",
    setMode,
    toggle: useCallback((): void => {
      setMode((m) => (m === "vulnerable" ? "csk" : "vulnerable"));
    }, []),
  };
}
